import React from "react";
import {MONTHS} from "../config";
import {
    FiCheckCircle,
    FiDatabase,
    FiInfo,
    FiLock,
    FiMail,
    FiRefreshCw,
    FiShare2,
    FiShield,
    FiTrash2
} from "react-icons/fi";

function Privacy() {
    const now = new Date();
    const last_update = MONTHS[now.getMonth()] + ' ' + now.getFullYear();
    return (
        <div className="pt-24 pb-16 text-[#1a1a1a] min-h-screen">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-12 text-center">
                    <div className="inline-block mb-6">
                        <div
                            className="w-24 h-1 bg-gradient-to-r from-white via-white/80 to-white mx-auto rounded-full shadow-lg shadow-white/50"></div>
                    </div>
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <FiShield className="text-4xl md:text-5xl text-[#C8102E]"/>
                        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#C8102E] to-[#A00D25] bg-clip-text text-transparent drop-shadow-lg">
                            Privacy Policy
                        </h1>
                    </div>
                    <p className="text-[#2d1810] text-lg max-w-3xl mx-auto mb-2">
                        KidKat Studio Data Protection Statement
                    </p>
                    <p className="text-[#2d1810]/70 text-sm">
                        Last Updated: {last_update}
                    </p>
                </div>

                {/* 1. Our Commitment to Your Privacy */}
                <div
                    className="mb-8 bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-2xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-white/20 rounded-lg border border-white/40">
                            <FiLock className="text-white text-2xl"/>
                        </div>
                        <h2 className="text-3xl font-bold text-white">Our Commitment to Your Privacy</h2>
                    </div>
                    <p className="text-white/95 text-lg leading-relaxed mb-4">
                        At KidKat Studio, your privacy is a core priority. This document outlines how we collect, use,
                        protect, and store your personal information when you interact with our games and related
                        services (collectively referred to as "the Services").
                    </p>
                    <p className="text-white/95 text-lg leading-relaxed">
                        By using the Services, you agree to the practices described in this Data Protection Statement.
                        We aim to provide full transparency and remain dedicated to safeguarding your data rights.
                    </p>
                </div>

                {/* 2. Personal Information We Collect */}
                <div className="mb-8">
                    <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-[#C8102E] to-[#A00D25] bg-clip-text text-transparent">
                        Personal Information We Collect
                    </h2>
                    <p className="text-[#2d1810] text-lg mb-6 text-center max-w-3xl mx-auto">
                        The information we gather helps us deliver, improve, and maintain the quality of our Services.
                        We collect the following categories of data:
                    </p>

                    <div className="grid md:grid-cols-3 gap-6">
                        {/* 2.1 Information You Provide Directly */}
                        <div
                            className="bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-xl p-6 shadow-lg border border-white/40 backdrop-blur-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-white/20 rounded-lg border border-white/40">
                                    <FiMail className="text-white text-xl"/>
                                </div>
                                <h3 className="text-xl font-bold text-white">Information You Provide</h3>
                            </div>
                            <p className="text-white/95 text-sm mb-3 leading-relaxed">
                                This includes any information you voluntarily submit—for example, during registration,
                                purchases, or when contacting support.
                            </p>
                            <ul className="text-white/90 text-sm space-y-2">
                                <li className="flex items-start gap-2">
                                    <span className="text-white mt-1">•</span>
                                    <span><strong>Registration Data:</strong> Your full name, username, email address, user ID, and login credentials.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-white mt-1">•</span>
                                    <span><strong>Support Interactions:</strong> Records of your communications with our support team or participation in studio-related activities.</span>
                                </li>
                            </ul>
                        </div>

                        {/* 2.2 Information Collected Automatically */}
                        <div
                            className="bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-xl p-6 shadow-lg border border-white/40 backdrop-blur-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-white/20 rounded-lg border border-white/40">
                                    <FiDatabase className="text-white text-xl"/>
                                </div>
                                <h3 className="text-xl font-bold text-white">Automatically Collected</h3>
                            </div>
                            <p className="text-white/95 text-sm mb-3 leading-relaxed">
                                When you use our Services, certain technical and usage data is logged automatically.
                            </p>
                            <ul className="text-white/90 text-sm space-y-2">
                                <li className="flex items-start gap-2">
                                    <span className="text-white mt-1">•</span>
                                    <span><strong>Technical Identifiers:</strong> Device IDs, IP address, hardware details, operating system version, and browser type.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-white mt-1">•</span>
                                    <span><strong>Gameplay Metrics:</strong> Information like playtime, level progression, purchased items, and your in-game performance.</span>
                                </li>
                            </ul>
                        </div>

                        {/* 2.3 Information from Linked Platforms */}
                        <div
                            className="bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-xl p-6 shadow-lg border border-white/40 backdrop-blur-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-white/20 rounded-lg border border-white/40">
                                    <FiShare2 className="text-white text-xl"/>
                                </div>
                                <h3 className="text-xl font-bold text-white">Linked Platforms</h3>
                            </div>
                            <p className="text-white/95 text-sm leading-relaxed">
                                If you connect your KidKat profile to third-party services (such as Apple Game Center,
                                Google Play Games, or social media), we may receive profile data based on the
                                permissions you've set on those platforms.
                            </p>
                        </div>
                    </div>
                </div>

                {/* 3. How We Use Your Information */}
                <div
                    className="mb-8 bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-2xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-white/20 rounded-lg border border-white/40">
                            <FiInfo className="text-white text-2xl"/>
                        </div>
                        <h2 className="text-3xl font-bold text-white">How We Use Your Information</h2>
                    </div>
                    <p className="text-white/95 text-lg mb-4 leading-relaxed">
                        We process your data solely for these essential purposes:
                    </p>
                    <ul className="text-white/95 space-y-3">
                        <li className="flex items-start gap-3">
                            <FiCheckCircle className="text-white mt-1 flex-shrink-0"/>
                            <span><strong className="text-white">Service Operation:</strong> To enable gameplay, manage your account, and ensure all features function properly.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <FiCheckCircle className="text-white mt-1 flex-shrink-0"/>
                            <span><strong className="text-white">Product Improvement:</strong> To analyze usage patterns, fix technical problems, and enhance game performance.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <FiCheckCircle className="text-white mt-1 flex-shrink-0"/>
                            <span><strong className="text-white">Customer Support:</strong> To address questions, requests, and support needs efficiently.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <FiCheckCircle className="text-white mt-1 flex-shrink-0"/>
                            <span><strong className="text-white">Security & Legal Compliance:</strong> To prevent fraud, enforce our Terms of Service, and meet legal or governmental requirements.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <FiCheckCircle className="text-white mt-1 flex-shrink-0"/>
                            <span><strong className="text-white">Personalized Experience:</strong> To tailor your gameplay experience based on your activity and preferences.</span>
                        </li>
                    </ul>
                </div>

                {/* 4. When Information Is Shared */}
                <div
                    className="mb-8 bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-2xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-white/20 rounded-lg border border-white/40">
                            <FiShare2 className="text-white text-2xl"/>
                        </div>
                        <h2 className="text-3xl font-bold text-white">When Information Is Shared</h2>
                    </div>
                    <p className="text-white/95 text-lg mb-4 leading-relaxed font-semibold">
                        KidKat Studio does not sell, rent, or trade your personal data for advertising or commercial
                        gain.
                    </p>
                    <p className="text-white/95 text-lg mb-4 leading-relaxed">
                        We only share your information in specific, limited situations:
                    </p>
                    <ul className="text-white/95 space-y-3">
                        <li className="flex items-start gap-3">
                            <FiCheckCircle className="text-white mt-1 flex-shrink-0"/>
                            <span><strong className="text-white">Legal Requirements:</strong> When required by law, court order, or when necessary to protect our rights, users, or the public.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <FiCheckCircle className="text-white mt-1 flex-shrink-0"/>
                            <span><strong className="text-white">Authorized Partners:</strong> With trusted third-party service providers who perform essential functions like data storage, analytics, or payment processing. These partners are bound by strict confidentiality obligations.</span>
                        </li>
                    </ul>
                </div>

                {/* 5. Data Security and Retention */}
                <div className="mb-8">
                    <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-[#C8102E] to-[#A00D25] bg-clip-text text-transparent">
                        Data Security and Retention
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* How We Protect Your Data */}
                        <div
                            className="bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-xl p-6 shadow-lg border border-white/40 backdrop-blur-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-white/20 rounded-lg border border-white/40">
                                    <FiShield className="text-white text-xl"/>
                                </div>
                                <h3 className="text-xl font-bold text-white">How We Protect Your Data</h3>
                            </div>
                            <p className="text-white/95 leading-relaxed">
                                We apply strong technical and administrative safeguards—such as encryption and
                                multi-factor authentication—to protect your information from unauthorized access,
                                alteration, or loss. Please ensure your own account credentials remain secure.
                            </p>
                        </div>

                        {/* Data Retention and Account Deletion */}
                        <div
                            className="bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-xl p-6 shadow-lg border border-white/40 backdrop-blur-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-white/20 rounded-lg border border-white/40">
                                    <FiTrash2 className="text-white text-xl"/>
                                </div>
                                <h3 className="text-xl font-bold text-white">Data Retention and Account Deletion</h3>
                            </div>
                            <p className="text-white/95 mb-3 leading-relaxed">
                                We keep your information for as long as your account is active or as needed to fulfill
                                the purposes of this policy.
                            </p>
                            <ul className="text-white/90 text-sm space-y-2">
                                <li className="flex items-start gap-2">
                                    <span className="text-white mt-1">•</span>
                                    <span><strong>Account Deactivation:</strong> You may close your account at any time through in-game settings or by contacting support.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-white mt-1">•</span>
                                    <span><strong>Backup Retention:</strong> Some data may remain in secure backups for up to six (6) months after deletion to satisfy legal requirements and maintain system integrity before being permanently erased.</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* 6. Your Data Rights */}
                <div
                    className="mb-8 bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-2xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-white/20 rounded-lg border border-white/40">
                            <FiCheckCircle className="text-white text-2xl"/>
                        </div>
                        <h2 className="text-3xl font-bold text-white">Your Data Rights</h2>
                    </div>
                    <p className="text-white/95 text-lg mb-4 leading-relaxed">
                        As a user of KidKat's Services, you have the following rights:
                    </p>
                    <ul className="text-white/95 space-y-3">
                        <li className="flex items-start gap-3">
                            <FiCheckCircle className="text-white mt-1 flex-shrink-0"/>
                            <span><strong className="text-white">Access, Correction, or Deletion:</strong> You may request to review, update, or permanently remove the personal information we hold.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <FiCheckCircle className="text-white mt-1 flex-shrink-0"/>
                            <span><strong className="text-white">Account Closure:</strong> You can choose to permanently close your account at any time, as described in Section 5.</span>
                        </li>
                    </ul>
                </div>

                {/* 7. Policy Updates */}
                <div
                    className="mb-8 bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-2xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-white/20 rounded-lg border border-white/40">
                            <FiRefreshCw className="text-white text-2xl"/>
                        </div>
                        <h2 className="text-3xl font-bold text-white">Policy Updates</h2>
                    </div>
                    <p className="text-white/95 text-lg leading-relaxed">
                        We may update this policy occasionally to reflect changes in our processes or legal obligations.
                        We will notify you of significant updates and display the "Last Updated" date at the top of this
                        document. Continued use of the Services after changes indicates your acceptance of the updated
                        policy.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default Privacy;
